import {
  getPinPath,
  getPinScale,
  getPinSize,
  PinIcon,
  PinSize,
  TEXT_COLOR,
} from '@/constants/canvas';
import { HALF, ONE_AND_HALF, QUARTER, THIRD, TWO_AND_HALF } from '@/constants/dividers';
import { DEFAULT_FONT_FAMILY } from '@/constants/font';
import { Point } from '@/types/map';
import { createCanvas } from '@/utils/canvas/getCanvas';
import { getFontSizeToFit } from '@/utils/canvas/getFontSizeToFit';
import { ONE } from '@/constants/common';

const SMALL_TEXT_VALUE = 1;
const MEDIUM_TEXT_VALUE = 10;
const BIG_TEXT_VALUE = 100;

export interface GetCanvasIconArgs {
  color: string;
  value: number;
  textColor?: string;
  pinIcon: PinIcon;
}

export const drawPinOnCanvas = (
  { color }: Pick<GetCanvasIconArgs, 'color'>,
  ctx: CanvasRenderingContext2D,
  pinType: PinIcon,
): void => {
  const path = new Path2D(getPinPath(pinType));
  ctx.fillStyle = color;

  const scale = getPinScale(pinType);
  ctx.scale(scale.x, scale.y);

  ctx.fill(path);
  ctx.scale(ONE / scale.x, ONE / scale.y);
};

export const getTextWidth = (value: number, pinSize: PinSize): number => {
  switch (true) {
    case value === SMALL_TEXT_VALUE:
      return pinSize.width / QUARTER;
    case value < MEDIUM_TEXT_VALUE:
      return pinSize.width / THIRD;
    case value < BIG_TEXT_VALUE:
      return pinSize.width / HALF;
    default:
      return pinSize.width / ONE_AND_HALF;
  }
};

export const getTextPosition = (
  textWidth: number,
  textHeight: number,
  pinSize: PinSize,
): Point => ({
  x: (pinSize.width - textWidth) / HALF,
  y: (pinSize.height - textHeight) / TWO_AND_HALF,
});

export const drawNumberOnCanvas = (
  { value, textColor, pinIcon }: Pick<GetCanvasIconArgs, 'value' | 'textColor' | 'pinIcon'>,
  ctx: CanvasRenderingContext2D,
): void => {
  const text = `${value}`;
  const textMetrics = ctx.measureText(text);

  const pinSize = getPinSize(pinIcon);
  const textWidth = getTextWidth(value, pinSize);
  const fontSize = getFontSizeToFit(ctx, text, DEFAULT_FONT_FAMILY, textWidth);
  const textHeight = textMetrics.fontBoundingBoxAscent + textMetrics.fontBoundingBoxDescent;
  const { x, y } = getTextPosition(textWidth, textHeight, pinSize);

  ctx.fillStyle = textColor || TEXT_COLOR;
  ctx.textBaseline = 'top';
  ctx.font = `${fontSize}px ${DEFAULT_FONT_FAMILY}`;
  ctx.fillText(text, x, y);
};

export const getCanvasIcon = (
  args: Omit<GetCanvasIconArgs, 'value'> & { value?: number },
): HTMLCanvasElement => {
  const canvas = createCanvas(getPinSize(args.pinIcon));
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }

  drawPinOnCanvas(args, ctx, args.pinIcon);

  if (args?.value !== undefined) {
    drawNumberOnCanvas(
      { value: args.value, textColor: args?.textColor, pinIcon: args.pinIcon },
      ctx,
    );
  }

  return canvas;
};
